<template>
  <a-modal
    v-model:open="visible"
    :footer="null"
    width="440px"
    class="pay-modal"
    :mask-closable="false"
  >
    <!-- 顶部标题区域 -->
    <div class="modal-head">
      <h2 class="modal-title">{{ payInfo.title }}</h2>
      <!-- <div class="sub-title">— 连续包月 —</div> -->
    </div>

    <!-- 价格优惠区 -->
    <div class="price-area">
      <span class="current-price">¥{{ payInfo.price }}</span>
      <div class="price-right">
        <span class="origin-price">¥{{ originPrice }}</span>
        <span class="discount-tag">购买活动优惠，立省20元</span>
      </div>
    </div>

    <!-- 分割线 -->
    <div class="divider-line"></div>

    <!-- 二维码区域 -->
    <div class="qrcode-box">
      <div class="qrcode-placeholder">
        <img v-if="codeUrl" :src="qrcodeSrc" alt="微信支付二维码" />
        <a-spin v-else />
      </div>
      <p class="qrcode-tip">
        请打开<span class="text-blue">微信</span>扫码支付
      </p>
    </div>

    <!-- 底部协议文字 -->
    <div class="agreement-tip">
      开通会员即表示同意<br />
      <!-- <span class="agreement-link">《新致会员服务协议》</span> -->
      <span class="agreement-link" @click="downloadAgreement">《用户协议》</span
      >和
      <span class="agreement-link" @click="downloadPrivacy">《隐私政策》</span>
    </div>

    <!-- 用户协议预览弹窗：docx-preview 本地渲染成只读 HTML，
         不走微软在线预览（其页面自带"编辑副本"按钮，跨域 iframe 无法隐藏） -->
    <a-modal
      v-model:open="agreementVisible"
      :title="agreementTitle"
      :footer="null"
      width="880px"
      class="agreement-modal"
    >
      <a-spin v-if="agreementLoading" class="agreement-spin" />
      <div
        v-show="!agreementLoading"
        ref="docxContainerEl"
        class="agreement-docx"
      ></div>
    </a-modal>
  </a-modal>
</template>

<script setup>
import { ref, computed, watch, onUnmounted } from "vue";
import { message } from "ant-design-vue";
import { renderAsync } from "docx-preview";
import {
  createWeChatNativePayOrder,
  queryWechatOrder,
  processPayment,
} from "../../api/purchase/recharge";

const props = defineProps(["open", "payInfo"]);
const emit = defineEmits(["update:open", "pay-success"]);
const visible = computed({
  get: () => props.open,
  set: (val) => emit("update:open", val),
});

/* 划线原价 = 现价 + 20。后端 price 可能是字符串，直接 + 会变成拼接，先转数字 */
const originPrice = computed(() => Number(props.payInfo?.price || 0) + 20);

// 二维码 codeUrl
const codeUrl = ref("");
const orderId = ref("");

// 轮询定时器
let pollTimer = null;

// 二维码图片地址（使用在线 API 生成）
const qrcodeSrc = computed(() => {
  if (!codeUrl.value) return "";
  return `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(
    codeUrl.value,
  )}`;
});

// 查询支付状态
const startPolling = () => {
  clearInterval(pollTimer);
  pollTimer = setInterval(async () => {
    try {
      const res = await queryWechatOrder(orderId.value);
      if (res.isSuccess && res.data?.tradeState === "SUCCESS") {
        clearInterval(pollTimer);
        pollTimer = null;
        // 支付成功，调用 ProcessPayment
        await processPayment(orderId.value, "微信支付");
        message.success("支付成功");
        visible.value = false;
        emit("pay-success");
      }
    } catch (err) {
      console.error("查询支付状态失败", err);
    }
  }, 2000);
};

// 弹窗打开时创建订单
watch(visible, async (val) => {
  if (val && props.payInfo?.activityId) {
    codeUrl.value = "";
    orderId.value = "";
    clearInterval(pollTimer);
    pollTimer = null;
    try {
      const res = await createWeChatNativePayOrder(props.payInfo.activityId);
      if (res.isSuccess && res.data?.codeUrl) {
        codeUrl.value = res.data.codeUrl;
        orderId.value = res.data.orderId || props.payInfo.activityId;
        // 开始轮询支付状态
        startPolling();
      }
    } catch (err) {
      console.error("创建支付订单失败", err);
    }
  } else if (!val) {
    // 弹窗关闭时清除定时器
    clearInterval(pollTimer);
    pollTimer = null;
  }
});

// 组件卸载时清除定时器
onUnmounted(() => {
  clearInterval(pollTimer);
});

/* ============ 协议弹窗预览 ============
 * docx-preview 把 docx 渲染成只读 HTML（纯展示，无任何编辑入口）。
 * 前提：zhifa.law 已开 CORS（Access-Control-Allow-Origin: *），浏览器可直接 fetch。 */
const AGREEMENT_URL = "https://zhifa.law/协议/新致新知《用户协议》.docx";
const PRIVACY_URL = "https://zhifa.law/协议/新致新知《隐私政策》.docx";
const agreementVisible = ref(false);
const agreementTitle = ref("用户协议");
const agreementLoading = ref(false);
// 容器用 v-show 常驻 DOM，保证 ref 随时可取
const docxContainerEl = ref(null);
const openAgreementModal = async (url, title) => {
  agreementTitle.value = title;
  agreementVisible.value = true;
  agreementLoading.value = true;
  let stage = "下载";
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const buf = await res.arrayBuffer();
    const container = docxContainerEl.value;
    if (!container) return;
    container.innerHTML = "";
    stage = "解析";
    await renderAsync(buf, container, null, {
      className: "agreement-docx-content",
      inWrapper: true,
    });
  } catch (err) {
    console.error(`协议文档${stage}失败:`, err);
    message.error(`文档${stage}失败：${err?.message || err}`);
  } finally {
    agreementLoading.value = false;
  }
};
const downloadAgreement = () => {
  openAgreementModal(AGREEMENT_URL, "新致新知《用户协议》");
};
const downloadPrivacy = () => {
  openAgreementModal(PRIVACY_URL, "新致新知《隐私政策》");
};
</script>

<style scoped lang="less">
.pay-modal {
  :deep(.ant-modal-content) {
    border-radius: 24px;
  }
  :deep(.ant-modal-close) {
    width: 36px;
    height: 36px;
    top: 16px;
    right: 16px;
    .ant-modal-close-x {
      font-size: 20px;
      color: #666;
    }
  }
  :deep(.ant-modal-body) {
    padding: 40px 30px 36px;
    text-align: center;
  }

  /* 顶部标题 */
  .modal-head {
    margin-bottom: 32px;
    .modal-title {
      font-size: 34px;
      color: #0f2141;
      margin: 0 0 10px;
      font-weight: 500;
      text-align: center;
    }
    .sub-title {
      font-size: 16px;
      color: #62728b;
      letter-spacing: 2px;
      text-align: center;
    }
  }

  /* 价格区域 左右排版 */
  .price-area {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 24px;
    margin-bottom: 24px;
    .current-price {
      font-size: 72px;
      color: #f25022;
      font-weight: 600;
      line-height: 1;
    }
    .price-right {
      display: flex;
      flex-direction: column;
      gap: 8px;
      align-items: flex-start;
      .origin-price {
        color: #94a3b8;
        text-decoration: line-through;
        font-size: 18px;
      }
      .discount-tag {
        background-color: #f25022;
        color: #ffffff;
        font-size: 14px;
        padding: 4px 12px;
        border-radius: 16px;
        white-space: nowrap;
      }
    }
  }

  /* 分割横线 */
  .divider-line {
    width: 100%;
    height: 1px;
    background-color: #e7edf7;
    margin-bottom: 36px;
  }

  /* 二维码模块 */
  .qrcode-box {
    margin: 0 auto 48px;
    width: 180px;
    .qrcode-placeholder {
      width: 180px;
      height: 180px;
      border: 1px solid #e7edf7;
      border-radius: 8px;
      overflow: hidden;
      display: flex;
      justify-content: center;
      align-items: center;
      img {
        width: 100%;
        height: 100%;
        object-fit: cover;
      }
    }
    .qrcode-tip {
      margin-top: 16px;
      font-size: 16px;
      color: #222;
      text-align: center;
      .text-blue {
        color: #1677ff;
      }
    }
  }

  /* 底部协议文字 */
  .agreement-tip {
    font-size: 15px;
    color: #62728b;
    line-height: 1.8;
    text-align: center;
    .agreement-link {
      color: #1677ff;
      cursor: pointer;
    }
  }
}

/* 用户协议预览弹窗 */
.agreement-modal {
  :deep(.ant-modal-body) {
    padding: 16px 20px;
  }
  .agreement-spin {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 70vh;
  }
  .agreement-docx {
    width: 100%;
    height: 70vh;
    overflow-y: auto;
    border-radius: 8px;
    background: #f5f7fa;
    /* docx-preview 默认给 .docx-wrapper 塞灰底+内边距，这里对齐原 iframe 观感 */
    :deep(.docx-wrapper) {
      background: #f5f7fa;
      padding: 24px 12px;
      gap: 16px;
    }
  }
}
</style>
